import { z } from "zod";
import { SectionKeySchema } from "./section-access.js";
/**
 * Reports of the agents as pages of Vekha (04.11.2026, Ден 2643; the reference — Alter's report_run). An agent or a script puts a
 * report (ops/bin/agent-report): its kind, its period, a short Telegram text and the data of the page; Vekha keeps one report per
 * kind and period and gives its address `/report/<id>`. In Telegram the text comes with a button that opens the page: `web_app` in a
 * private chat, a Direct Link Mini App (`startapp=report_<id>`) in a group. Who reads it — by a section of Vekha (`section`).
 */
/** Who sends the report in Telegram: the bot of Yana or of Arkadiy. */
export const ReportBotSchema = z.enum(["yana", "arkadiy"]);
/**
 * The kinds of report and the section that opens each by default (the report may name another one). Every kind is the same page of
 * blocks; a kind says who builds it and who reads it. `blocks` — a page the agent hands over whole. The three reports of 05.11.2026
 * are built by Vekha itself from the evidence their scripts already write and the agent's judgment (apps/api/src/reports/builders):
 * the numbers come from code, the agent writes only the conclusions, who needs attention and why.
 * - `yana_evening` — Yana's evening report (new applications and returns of the day, her correspondence, her zone), to Ден;
 * - `rop_night` — Arkadiy's night review of the sales department, to Ден and the head of sales;
 * - `weekly` — the week for the leaders' chat.
 */
export const REPORT_KINDS = {
    blocks: { section: "analytics_summary" },
    yana_evening: { section: "analytics_sales" },
    rop_night: { section: "analytics_sales" },
    weekly: { section: "analytics_summary" },
};
export const ReportKindSchema = z.enum(Object.keys(REPORT_KINDS));
/** A day `2026-11-04`, days `2026-11-03..2026-11-09`, a month `2026-11`. */
const DAY = /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/u;
export const ReportPeriodSchema = z.string().refine((value) => {
    if (/^\d{4}-(?:0[1-9]|1[0-2])$/u.test(value))
        return true;
    const [from, to, extra] = value.split("..");
    if (extra !== undefined || !from || !DAY.test(from))
        return false;
    return to === undefined || (DAY.test(to) && to >= from);
}, "Период — день 2026-11-04, дни 2026-11-03..2026-11-09 или месяц 2026-11.");
const UUID = z.string().uuid();
const Text = (max) => z.string().trim().min(1).max(max);
const Amount = z.number().finite().min(-1e12).max(1e12);
export const ReportUnitSchema = z.enum(["count", "eur", "rub", "percent"]);
/**
 * A row behind a number or of a list: a person or a deal. A deal of Vekha opens its dialog (`/dialogs?deal=…&conversation=…`) for a
 * reader with «Диалоги», an amoCRM lead its card in amoCRM; a row without either is a plain line.
 */
export const ReportRowSchema = z.object({
    name: Text(120),
    note: Text(240).optional(),
    /** The agent's words about this person (05.11.2026, Ден: «что с ним не так и почему он ещё не записался»): a line of its own under the row. */
    comment: Text(400).optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}(?:T[\d:.]+(?:Z|[+-]\d{2}:\d{2}))?$/u).optional(),
    amount: Amount.optional(),
    unit: ReportUnitSchema.optional(),
    dealId: UUID.optional(),
    conversationId: UUID.optional(),
    amoLeadId: z.string().regex(/^[1-9]\d{0,15}$/u).optional(),
}).strict();
const Rows = z.array(ReportRowSchema).max(500);
/** A screen of Vekha a number leads to (`/analytics/entries?from=…`, `/dialogs?view=yana_day&date=…`): a path, never an address. */
const InternalPath = z.string().max(300).regex(/^\/[a-z][a-z0-9/_-]*(?:\?[\w.=&%:-]*)?$/u);
/** A number of a report: with `rows` it opens them (the collector puts them: a number that «Аналитика» has not counts here too). */
export const ReportNumberSchema = z.object({
    label: Text(80),
    value: Amount.nullable(),
    unit: ReportUnitSchema.default("count"),
    /** The same number of the period before: the change is shown next to it. */
    previous: Amount.nullable().optional(),
    /** Which way is good for the change: up (sales), down (refusals) or neither. */
    good: z.enum(["up", "down", "none"]).default("up"),
    note: Text(160).optional(),
    rows: Rows.optional(),
    /** Without rows: the screen of Vekha that has them (a number «Аналитика» counts too opens there, for a reader of that section). */
    link: InternalPath.optional(),
}).strict();
/** How a part of a split reads: good (booked, bought), waiting, bad (cancelled, lost), or plain. */
export const ReportToneSchema = z.enum(["good", "wait", "bad", "plain"]);
/** A part of a whole (the outcomes of the day's applications): its count and the people behind it. */
export const ReportPartSchema = z.object({ label: Text(60), value: z.number().int().min(0).max(100_000), tone: ReportToneSchema.default("plain"), rows: Rows.optional() }).strict();
/**
 * A judgment of the agent about a case: who (a row, linked as any row) and what — the conclusion, why it needs attention, the next
 * step. `red` costs money or a client today, `yellow` can wait for the day.
 */
export const ReportCaseSchema = z.object({ row: ReportRowSchema.optional(), text: Text(1200), tone: z.enum(["red", "yellow"]).optional() }).strict();
/** A cell of a table: a number, maybe with its rows. */
export const ReportCellSchema = z.object({ value: Amount.nullable(), unit: ReportUnitSchema.default("count"), rows: Rows.optional() }).strict();
export const ReportBlockSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("numbers"), title: Text(80).optional(), items: z.array(ReportNumberSchema).min(1).max(12) }).strict(),
    z.object({ type: z.literal("people"), title: Text(80), note: Text(240).optional(), rows: Rows }).strict(),
    z.object({ type: z.literal("text"), title: Text(80).optional(), text: Text(4000) }).strict(),
    /** A whole and its parts on one bar (what became of the day's applications); the numbers under it are of the same people. */
    z.object({
        type: z.literal("split"), title: Text(80), note: Text(240).optional(), total: z.number().int().min(0).max(100_000),
        parts: z.array(ReportPartSchema).min(1).max(8), items: z.array(ReportNumberSchema).max(4).optional(),
    }).strict(),
    /** The agent's cases: one line each, the person first. */
    z.object({ type: z.literal("cases"), title: Text(80), note: Text(240).optional(), items: z.array(ReportCaseSchema).min(1).max(40) }).strict(),
    /** A small table (the managers of the day): a row per person or thing, a column per number; a cell with rows opens them. */
    z.object({
        type: z.literal("table"), title: Text(80), note: Text(240).optional(), columns: z.array(Text(40)).min(1).max(6),
        rows: z.array(z.object({ label: Text(80), cells: z.array(ReportCellSchema.nullable()).min(1).max(6) }).strict()).min(1).max(20),
    }).strict(),
]);
/** A deal of Vekha behind an amoCRM lead: a `#23974244` in a text leads to its dialog (Vekha fills it when it saves a built report). */
export const ReportLeadLinkSchema = z.object({ dealId: UUID, conversationId: UUID.optional() }).strict();
export const AMO_LEAD_IN_TEXT = /#([1-9]\d{5,11})\b/gu;
export const ReportBlocksDataSchema = z.object({
    /** When the numbers were taken; `complete: false` — the source said the day is not whole yet. */
    asOf: z.string().datetime({ offset: true }).optional(),
    complete: z.boolean().optional(),
    leads: z.record(z.string().regex(/^[1-9]\d{0,15}$/u), ReportLeadLinkSchema).optional()
        .refine((value) => value === undefined || Object.keys(value).length <= 1000, "Не больше 1000 сделок."),
    blocks: z.array(ReportBlockSchema).min(1).max(30),
}).strict();
/** The data of a page by its kind: one page of blocks for every kind. */
export const REPORT_DATA_SCHEMAS = {
    blocks: ReportBlocksDataSchema, yana_evening: ReportBlocksDataSchema, rop_night: ReportBlocksDataSchema, weekly: ReportBlocksDataSchema,
};
/** What the agent hands over (stdin of ops/bin/agent-report save): the page's title, the Telegram text, the data. */
export const ReportSaveInputSchema = z.object({
    title: Text(120),
    /** The Telegram text (Telegram HTML, 5–8 short lines); the button to the page is added by Vekha. */
    text: Text(3_800),
    /** Who reads the page, when not the kind's own section. */
    section: SectionKeySchema.optional(),
    data: z.unknown(),
}).strict();
const ReportViewBase = {
    id: UUID,
    period: ReportPeriodSchema,
    title: Text(120),
    bot: ReportBotSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** The address of an amoCRM lead without its id (`https://<account>/leads/detail/`): a row's `amoLeadId` opens there. */
    amoLeadUrlBase: z.string().url().nullable(),
};
/** GET /api/reports/:id — the page of a report, for a reader of its section. */
export const ReportViewSchema = z.object({ ...ReportViewBase, kind: ReportKindSchema, data: ReportBlocksDataSchema }).strict();
/** The start parameter of a report's Direct Link (telegram-login.ts routes `report_<id>` to `/report/<id>`). */
export function reportStartParam(id) {
    return `report_${id}`;
}
//# sourceMappingURL=reports.js.map